import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { applyBranding } from "@/hooks/useBranding";
import { TriangleAlert, Sparkles, Loader2 } from "lucide-react";

export default function Verify() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [err, setErr] = useState(null);
  const ran = useRef(false);

  const token = params.get("token");
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    applyBranding({ brand_primary: "#10B981" });
    if (!token) { setErr("Missing sign-in token."); return; }
    api.post("/auth/magic/verify", { token })
      .then(({ data }) => {
        localStorage.setItem("hs_token", data.token);
        const r = data.user.role;
        window.location.href = r === "recruiter" ? "/recruiter" : r === "admin" ? "/admin" : "/candidate";
      })
      .catch((e) => setErr(e.response?.data?.detail || "This link is invalid or expired."));
  }, [token]);

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#050807] px-4" data-testid="verify-page">
      <div className="pointer-events-none absolute inset-0 grain-overlay opacity-40" />
      <div className="pointer-events-none absolute -top-1/4 left-1/3 h-[36rem] w-[36rem] rounded-full bg-emerald-500/20 blur-[140px] animate-pulse" />
      <div className="pointer-events-none absolute -bottom-1/4 right-1/4 h-[30rem] w-[30rem] rounded-full bg-teal-600/15 blur-[140px]" />
      <div className="pointer-events-none absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "linear-gradient(#10B981 1px,transparent 1px),linear-gradient(90deg,#10B981 1px,transparent 1px)", backgroundSize: "48px 48px" }} />

      {err ? (
        <div className="relative text-center space-y-4 max-w-sm">
          <div className="h-14 w-14 rounded-2xl bg-amber-500/15 flex items-center justify-center mx-auto"><TriangleAlert className="h-7 w-7 text-amber-400" /></div>
          <div className="font-display font-semibold text-white text-lg">{err}</div>
          <p className="text-slate-400 text-sm">Magic links are single-use and expire quickly. Request a fresh one to continue.</p>
          <button onClick={() => nav("/login")} className="text-sm text-primary hover:underline" data-testid="verify-login-link">Go to sign in</button>
        </div>
      ) : (
        <div className="relative text-center space-y-5">
          <div className="relative h-16 w-16 mx-auto">
            <div className="absolute inset-0 rounded-full bg-primary/20 animate-ping" />
            <div className="relative h-16 w-16 rounded-full bg-primary/15 flex items-center justify-center"><Sparkles className="h-7 w-7 text-primary" /></div>
          </div>
          <div className="text-slate-200 font-display text-lg flex items-center gap-2 justify-center"><Loader2 className="h-4 w-4 animate-spin" /> Signing you in securely…</div>
        </div>
      )}
    </div>
  );
}
